import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { createPortalTheme } from "../theme/createPortalTheme";
import PortalScrollGlobalStyles from "./portal/PortalScrollGlobalStyles";
import {
  AuthSidebar,
  FloatingLuBadge,
  LockedSidebar,
  PortalHeroBanner,
  PortalNavBar,
  ProfileChip,
  ProfileDropdown,
} from "./portal/PortalUI";
import AuthenticatedHero from "./portal/AuthenticatedHero";
import PortalBody from "./portal/PortalBody";
import QrLogin from "./QrLogin";
import LULogo from "../assets/LU-2026--Main-Revised.png";
import doctorsIllustration from "../assets/undraw_doctors_djoj.svg";
import paymentIllustration from "../assets/Payment Information-bro.svg";
import insuranceIllustration from "../assets/Insurance-amico.svg";
import businessPlanIllustration from "../assets/Business Plan-pana.svg";
import securityIllustration from "../assets/Security-cuate.svg";
import { jwtDecode } from "jwt-decode";

let hasFetchedPortalFlag = false;

const IndustryPortal = ({ industry }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [qrKey, setQrKey] = useState(0);
  const navigate = useNavigate();

  const AUTH_KEY = `Authenticated_${industry}`;
  const DATA_KEY = `data_${industry}`;

  const [isCardLocked, setIsCardLocked] = useState(false);
  const [refillStatus, setRefillStatus] = useState({});
  const [telehealthActive, setTelehealthActive] = useState(false);
  const [launchingApp, setLaunchingApp] = useState(null);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [claimType, setClaimType] = useState("Auto");
  const [claimStatusText, setClaimStatusText] = useState("Your claim #CLM-2026-092 is approved.");
  const [transferTarget, setTransferTarget] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [transferSuccess, setTransferSuccess] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code && !hasFetchedPortalFlag) {
      hasFetchedPortalFlag = true;
      setIsVerifying(true);

      const LEGIT_CLIENT_ID = "2307586";
      const REDIRECT_URI = window.location.origin + window.location.pathname;

      fetch("https://api.legitimatelyu.com/api/v1/user/oauth/token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: LEGIT_CLIENT_ID, code }),
      })
        .then((r) => r.json())
        .then((resp) => {
          if (resp.success && resp.result) {
            const { access_token, user: respUser } = resp.result;
            let decoded = respUser;
            try { decoded = { ...respUser, ...jwtDecode(access_token) }; } catch (_) { }
            localStorage.setItem(AUTH_KEY, "true");
            localStorage.setItem(DATA_KEY, JSON.stringify({ user: { ...respUser, user_token: access_token } }));
            setUser(decoded);
            window.history.replaceState({}, document.title, REDIRECT_URI);
          }
        })
        .catch((err) => console.error("Portal SSO failed:", err))
        .finally(() => setIsVerifying(false));
    } else {
      const storedAuth = localStorage.getItem(AUTH_KEY);
      const storedData = localStorage.getItem(DATA_KEY);
      if (storedAuth === "true" && storedData) {
        try {
          const parsed = JSON.parse(storedData);
          if (parsed?.user) {
            let decoded = parsed.user;
            if (parsed.user.user_token) {
              try { decoded = { ...parsed.user, ...jwtDecode(parsed.user.user_token) }; } catch (_) { }
            }
            setUser(decoded);
          }
        } catch (_) { }
      }
    }
  }, [AUTH_KEY, DATA_KEY]);

  useEffect(() => () => { hasFetchedPortalFlag = false; }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    html.classList.remove("hide-scrollbar");
    html.classList.add("portal-scroll-enabled");
    body.classList.add("portal-scroll-enabled");
    root?.classList.add("portal-scroll-enabled");
    return () => {
      html.classList.remove("portal-scroll-enabled");
      body.classList.remove("portal-scroll-enabled");
      root?.classList.remove("portal-scroll-enabled");
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(DATA_KEY);
    localStorage.removeItem("Authenticated");
    localStorage.removeItem("data");
    hasFetchedPortalFlag = false;
    setUser(null);
    setQrKey((k) => k + 1);
    if (window.location.search) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleQrLoginSuccess = useCallback((loginData) => {
    if (loginData?.user) {
      let decoded = loginData.user;
      if (loginData.user.user_token) {
        try { decoded = { ...loginData.user, ...jwtDecode(loginData.user.user_token) }; } catch (_) { }
      }
      localStorage.setItem(AUTH_KEY, "true");
      localStorage.setItem(DATA_KEY, JSON.stringify(loginData));
      setUser(decoded);
    }
    setIsModalOpen(false);
  }, [AUTH_KEY, DATA_KEY]);

  const themes = {
    finance: { name: "LegitiBank", primary: "#1e3a8a", bg: "#f8fafc", accent: "#3b82f6", text: "#0f172a", borderRadius: "8px", font: "'Inter', sans-serif", layout: "side-nav" },
    healthcare: { name: "HealPath Portal", primary: "#0d9488", bg: "#ffffff", accent: "#2dd4bf", text: "#134e4a", borderRadius: "24px", font: "'Outfit', sans-serif", layout: "side-nav" },
    insurance: { name: "SureGuard Login", primary: "#a65215", bg: "#faf5f0", accent: "#e9a026", text: "#3d200f", borderRadius: "16px", font: "'Inter', sans-serif", layout: "side-nav" },
    government: { name: "GovDirect Citizen Portal", primary: "#1e3a8a", bg: "#f8fafc", accent: "#3b82f6", text: "#0f172a", borderRadius: "12px", font: "'Inter', sans-serif", layout: "side-nav" },
    corporate: { name: "EnterpriseHub SSO", primary: "#4338ca", bg: "#020617", accent: "#6366f1", text: "#f8fafc", borderRadius: "12px", font: "'Inter', sans-serif", layout: "side-nav" },
  };

  const theme = themes[industry] || themes.corporate;
  const muiTheme = createPortalTheme(industry);

  const authPrompts = {
    finance: "Connect your LegitimatelyU secure profile to unlock asset allocation, private trading lines, and instant biometric money transfers.",
    healthcare: "Connect your LegitimatelyU secure profile to unlock patient records, telehealth consultations, and secure prescription management.",
    insurance: "Connect your LegitimatelyU secure profile to unlock policy management, claims filing, and continuous identity verification.",
    government: "Connect your LegitimatelyU secure profile to unlock citizen credentials, federal resources, and national-grade digital identity services.",
    corporate: "Connect your LegitimatelyU secure profile to unlock enterprise SSO apps, workforce access, and unified biometric sign-on.",
  };

  const portalShell = {
    finance: { icon: "🏛️", brand: "LegitiBank", lockedNav: ["Portfolio Summary", "Market Intel", "Secure Transfers", "Card Controls", "Investment Desk", "Compliance Audit"], authNav: ["Portfolio", "Market Data", "Trades", "Cards", "Transfers", "Security Settings"], heroBadge: { icon: "⚡", text: "Next-Gen Private Wealth Management" }, heroTitle: "Welcome to LegitiBank Institutional", illustration: paymentIllustration, ssoText: "LegitimatelyU verified sessions protect your wealth.", welcomeSubtitle: "Secure corporate banking portal active.", sidebarBg: "#fff" },
    healthcare: { icon: "🏥", brand: "HealPath", lockedNav: ["Appointments", "Telehealth Hub", "Vitals Monitor", "Prescriptions", "Lab Reports", "Health Records"], authNav: ["Dashboard", "Appointments", "Telehealth", "Prescriptions", "Vitals", "Records"], heroBadge: { icon: "🏥", text: "HEALPATH CLINIC & DIAGNOSTICS" }, heroTitle: "Welcome to HealPath Portal", illustration: doctorsIllustration, ssoText: "LegitimatelyU verified sessions protect your health data.", welcomeSubtitle: "Patient Identity Verified and encrypted locally via LegitimatelyU.", sidebarBg: "#fff" },
    insurance: { icon: "🛡️", brand: "SureGuard", lockedNav: ["My Policies", "Claims Center", "Billing", "Coverage Docs", "Roadside Assist", "Account Settings"], authNav: ["Policies", "Claims", "Billing", "Documents", "Coverage", "Settings"], heroBadge: { icon: "🛡️", text: "SUREGUARD CUSTOMER PROTECTION" }, heroTitle: "Welcome to SureGuard Customer Portal", illustration: insuranceIllustration, ssoText: "LegitimatelyU verified sessions protect your policies and claims.", welcomeSubtitle: "Policyholder portal active • Cryptographic Identity Active", sidebarBg: "#fff" },
    government: { icon: "🏛️", brand: "GovDirect", lockedNav: ["Citizen Credentials", "Document Vault", "Tax Services", "Benefits Portal", "Identity Level", "Admin Log"], authNav: ["Credentials", "Document Vault", "Tax Records", "Benefits", "Identity", "Portal Log"], heroBadge: { icon: "🏛️", text: "Federal Digital Identity Services" }, heroTitle: "Citizen Identity Access Terminal", illustration: securityIllustration, ssoText: "LegitimatelyU verified sessions protect your citizen credentials.", welcomeSubtitle: "Biometric Access Level 3 Active", sidebarBg: "#fff" },
    corporate: { icon: "⚡", brand: "EnterpriseHub", lockedNav: ["SSO Launchpad", "App Directory", "Device Security", "Audit Trail", "Workforce", "Admin Console"], authNav: ["Launchpad", "Applications", "Security", "Devices", "Audit Log", "Settings"], heroBadge: { icon: "⚡", text: "ENTERPRISE SSO LAUNCHPAD" }, heroTitle: "EnterpriseHub SSO Launchpad", illustration: businessPlanIllustration, ssoText: "LegitimatelyU verified sessions protect enterprise workforce access.", welcomeSubtitle: "Biometric SSO active • Session Trusted", sidebarBg: "#0f172a", darkSidebar: true },
  };

  const shell = portalShell[industry] || portalShell.finance;
  const authPrompt = authPrompts[industry] || authPrompts.finance;

  const isHashName = user?.full_name?.length >= 32 && !user?.full_name?.includes(" ");
  const displayName = user
    ? (isHashName && user?.email ? user.email.split("@")[0] : (user?.full_name || user?.fullName || user?.name || user?.email || "User"))
    : null;

  const heroBanner = (
    <PortalHeroBanner
      shell={shell}
      themeColors={theme}
      authPrompt={authPrompt}
      onLogin={() => setIsModalOpen(true)}
      illustration={shell.illustration}
    />
  );

  const authenticatedHero = (
    <AuthenticatedHero industry={industry} theme={theme} shell={shell} displayName={displayName} />
  );

  const profileMenu = user && (
    <Box sx={{ position: "relative" }}>
      <ProfileChip
        displayName={displayName}
        themeColors={theme}
        isOpen={isProfileOpen}
        onClick={() => setIsProfileOpen((p) => !p)}
      />
      {isProfileOpen && (
        <ProfileDropdown
          displayName={displayName}
          user={user}
          themeColors={theme}
          isDark={theme.bg === "#020617"}
          onSignOutClick={() => { setIsProfileOpen(false); setConfirmLogout(true); }}
          onClose={() => setIsProfileOpen(false)}
        />
      )}
    </Box>
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <PortalScrollGlobalStyles />
      <Box
        sx={{
          bgcolor: theme.bg,
          color: theme.text,
          minHeight: { xs: "100dvh", lg: "100vh" },
          fontFamily: theme.font,
          position: "relative",
          overflowX: { xs: "hidden", lg: "clip" },
          overflowY: "visible",
          pb: { xs: "calc(16px + env(safe-area-inset-bottom, 0px))", lg: 0 },
        }}
      >
        <FloatingLuBadge />

        <Backdrop open={isVerifying} sx={{ zIndex: 9999, flexDirection: "column", gap: 2.5, bgcolor: "rgba(0,0,0,0.65)" }}>
          <Box component="img" src={LULogo} alt="LU" sx={{ height: 60, objectFit: "contain" }} />
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>Verifying your identity…</Typography>
          <CircularProgress color="inherit" size={40} />
        </Backdrop>

        <QrLogin key={qrKey} onLogin={isModalOpen} onSkip={() => setIsModalOpen(false)} setMainData={handleQrLoginSuccess} />

        <PortalNavBar
          brand={theme.name}
          industry={industry}
          onHome={() => navigate("/")}
          user={user}
          profileMenu={profileMenu}
          themeColors={theme}
        />

        {theme.layout === "side-nav" && (
          <Grid container sx={{ alignItems: "stretch", minHeight: { xs: "auto", lg: "calc(100vh - 80px)" } }}>
            <Grid size={{ xs: 12, lg: 9 }} sx={{ order: { lg: 2 }, minWidth: 0, zIndex: 1 }}>
              <Box sx={{ p: { xs: 1.5, sm: 2.5, md: user ? 5 : "50px 60px" }, maxWidth: "100%" }}>
                <PortalBody
                  user={user}
                  industry={industry}
                  theme={theme}
                  shell={shell}
                  displayName={displayName}
                  authenticatedHero={authenticatedHero}
                  heroBanner={heroBanner}
                  isClaimModalOpen={isClaimModalOpen}
                  setIsClaimModalOpen={setIsClaimModalOpen}
                  claimType={claimType}
                  setClaimType={setClaimType}
                  setClaimStatusText={setClaimStatusText}
                  isCardLocked={isCardLocked}
                  setIsCardLocked={setIsCardLocked}
                  refillStatus={refillStatus}
                  setRefillStatus={setRefillStatus}
                  telehealthActive={telehealthActive}
                  setTelehealthActive={setTelehealthActive}
                  launchingApp={launchingApp}
                  setLaunchingApp={setLaunchingApp}
                  transferTarget={transferTarget}
                  setTransferTarget={setTransferTarget}
                  transferAmount={transferAmount}
                  setTransferAmount={setTransferAmount}
                  transferSuccess={transferSuccess}
                  setTransferSuccess={setTransferSuccess}
                />
              </Box>
            </Grid>

            <Grid
              size={{ xs: 12, lg: 3 }}
              sx={{
                order: { lg: 1 },
                width: { lg: 260 },
                flexShrink: 0,
                display: "block",
                position: { lg: "sticky" },
                top: { lg: 0 },
                alignSelf: { lg: "start" },
                height: { lg: "calc(100vh - 80px)" },
                px: { xs: 1.5, lg: 0 },
                pt: { xs: 0, lg: 0 },
              }}
            >
              {user ? (
                <AuthSidebar shell={shell} themeColors={theme} />
              ) : (
                <LockedSidebar shell={shell} themeColors={theme} />
              )}
            </Grid>
          </Grid>
        )}

        {/* Sign-out confirmation dialog — lives at portal level so it survives dropdown unmount */}
        <Dialog
          open={confirmLogout}
          onClose={() => setConfirmLogout(false)}
          slotProps={{ paper: { sx: { minWidth: { xs: "90%", sm: "400px" }, borderRadius: 2 } } }}
        >
          <DialogTitle sx={{ fontWeight: 800 }}>Confirm Sign Out</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: 15 }}>
              Are you sure you want to sign out?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setConfirmLogout(false)}
              sx={{ color: theme.primary, borderColor: `${theme.primary}40`, borderWidth: 1, fontWeight: 600, px: 3, "&:hover": { borderWidth: 1, borderColor: theme.primary } }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={() => { setConfirmLogout(false); handleLogout(); }}
              sx={{ color: "#dc2626", borderColor: "#dc262640", borderWidth: 1, fontWeight: 600, px: 3, "&:hover": { borderWidth: 1, borderColor: "#dc2626", bgcolor: "#ff00000a" } }}
              autoFocus
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default IndustryPortal;
